import { Box, Divider, Stack, Typography } from "@mui/material";
import { format } from "date-fns";
import { enUS, es, ptBR } from "date-fns/locale";
import { useTranslation } from "react-i18next";

import { IIndividualBeneficiaryData } from "../../../../models/individual-beneficiary-data";

interface IBeneficiarySituationDetailsModalProps {
  data: IIndividualBeneficiaryData;
}

const translationPath =
  "page.4uSiteApp.consultationsAndLogs.beneficiarySituation.";

const formatDate = (value?: string, language?: string) => {
  if (!value) return "-";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";

  return format(date, value.includes("T") ? "Pp" : "P", {
    locale: language === "pt-BR" ? ptBR : language === "en-US" ? enUS : es,
  }).replaceAll(",", "");
};

const Field = ({ label, value }: { label: string; value?: unknown }) => (
  <Box sx={{ minWidth: 180 }}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
      {value === null || value === undefined || value === ""
        ? "-"
        : String(value)}
    </Typography>
  </Box>
);

const BeneficiarySituationDetailsModal: React.FC<
  IBeneficiarySituationDetailsModalProps
> = ({ data }) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const label = (key: string, defaultValue: string) =>
    t(translationPath + key, { defaultValue });

  return (
    <Stack spacing={2} sx={{ minWidth: 720, maxWidth: 960 }}>
      <Typography variant="h6">
        {label("details", "Dados completos")}
      </Typography>

      <Stack direction="row" gap={2} flexWrap="wrap">
        <Field
          label={label("person_code", "Codigo da pessoa")}
          value={data.individualIdentifier}
        />
        <Field
          label={label("name", "Nome")}
          value={data.individualName}
        />
        <Field
          label={label("insured_code", "Codigo do segurado")}
          value={data.beneficiaryIdentifier}
        />
        <Field
          label={label("plan", "Plano")}
          value={data.planName}
        />
        <Field
          label={label("card_code", "Codigo da carteirinha")}
          value={data.cardCode}
        />
        <Field
          label={label("card_validity", "Validade Carteirinha")}
          value={formatDate(data.cardValidity, language)}
        />
        <Field
          label={label("contract_status", "Situacao Contrato")}
          value={data.contractSituation}
        />
      </Stack>

      <Divider />

      <Stack direction="row" gap={2} flexWrap="wrap">
        <Field
          label={label(
            "beneficiary_contract_date",
            "Contratacao do beneficiario",
          )}
          value={formatDate(data.beneficiaryContractDate, language)}
        />
        <Field
          label={label("beneficiary_included_date", "Inclusao na operadora")}
          value={formatDate(data.beneficiaryIncludedDate, language)}
        />
        <Field
          label={label(
            "beneficiary_released_date",
            "Liberacao do beneficiario",
          )}
          value={formatDate(data.beneficiaryReleasedDate, language)}
        />
        <Field
          label={label(
            "beneficiary_termination_date",
            "Rescisao do beneficiario",
          )}
          value={formatDate(data.beneficiaryTerminationDate, language)}
        />
        <Field
          label={label(
            "beneficiary_reactivation_date",
            "Reativacao do beneficiario",
          )}
          value={formatDate(data.beneficiaryReactivationDate, language)}
        />
        <Field
          label={label(
            "beneficiary_limit_utilization_date",
            "Limite de utilizacao do beneficiario",
          )}
          value={formatDate(data.beneficiaryLimitUtilizationDate, language)}
        />
        <Field
          label={label(
            "beneficiary_cancelation_date",
            "Cancelamento do beneficiario",
          )}
          value={formatDate(data.beneficiaryCancelationDate, language)}
        />
        <Field
          label={label("contract_approval_date", "Aprovacao do contrato")}
          value={formatDate(data.contractApprovalDate, language)}
        />
        <Field
          label={label("contract_termination_date", "Rescisao do contrato")}
          value={formatDate(data.contractTerminationDate, language)}
        />
        <Field
          label={label("contract_reactivation_date", "Reativacao do contrato")}
          value={formatDate(data.contractReactivationDate, language)}
        />
        <Field
          label={label("contract_cancelation_date", "Cancelamento do contrato")}
          value={formatDate(data.contractCancelationDate, language)}
        />
        <Field
          label={label(
            "contract_limit_utilization_date",
            "Limite de utilizacao do contrato",
          )}
          value={formatDate(data.contractLimitUtilizationDate, language)}
        />
        <Field
          label={label("contract_initial_date", "Inicio de vigencia")}
          value={formatDate(data.contractInitialDate, language)}
        />
      </Stack>
    </Stack>
  );
};

export default BeneficiarySituationDetailsModal;
